import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CustomPaper from '../common/CustomPaper';
import SelectProject from '../Component/SelectProject';
import { send_component_save_request, send_request } from '../../utils/Request';
import AddIcon from '@mui/icons-material/Add';
import './QueryDatabaseStyle.css';
import useQueryDatabaseStore, { getQueryDatabaseStore } from './useQueryDatabaseStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { DataGrid } from '@mui/x-data-grid';
import TextIcon from '../Component/Icons/TextIcon';
import NumberIcon from '../Component/Icons/NumberIcon';
import BooleanIcon from '../Component/Icons/BooleanIcon';
import DatetimeIcon from '../Component/Icons/DatetimeIcon';
import { getCurrentTime } from '../../utils/getCurrentTime';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import ListIcon from '../Component/Icons/ListIcon';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import { QUERY_OUTPUT_TYPES } from './QueryDatabaseUtil';
import RenderQueryItem from '../Component/RenderQueryItem';

const paperStyles = {
  background: '#ffffff',
  borderRadius: 8,
  boxShadow:
    '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
};

const QueryDatabaseTemplate = (props) => {
  const [availableColumns, setAvailableColumns] = useState([]);
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [databaseList, setDatabaseList] = useState([]);
  const [loadingScreen, setLoadingScreen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validateKey, setValidateKey] = useState(false);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);
  const [thisFocus, setThisFocus] = useState(null);

  const componentName = useProjectStore((s) => s.componentName);
  const componentDescription = useProjectStore((s) => s.componentDescription);
  const selectedPlaceholder = useProjectStore((s) => s.selectedPlaceholder);

  const store = useMemo(() => getQueryDatabaseStore(props.component_id), [props.component_id]);
  const storeData = store((s) => s.data);

  useEffect(() => {
    if (props.component_id === undefined) return;
    setLoadingScreen(true);

    // Skip primary component data fetch if already loaded (prefilled from keyed store)
    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoadingScreen(false);
      props.setChildTemplateLoaded(true);
    }

    // Secondary fetch: database list (always needed)
    send_request(`database/get_databases`, '', {}, 'GET').then((response) => {
      if (response.data) {
        setDatabaseList(response.data);

        if (store.getState().loaded) return;

        send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          '',
          {}
        ).then((response) => {
          if (response?.data?.components[0]?.componentData) {
            props.onComponentNameChanged(response.data.components[0].componentData.name);
            props.onComponentDescriptionChanged(response.data.components[0].description);
            props.onLastModifiedChanged(response.data.components[0].componentData.lastModified);

            let queryData = response.data.components[0].componentData.data;
            let componentDataObj = response.data.components[0];
            if (queryData.targetDatabaseId) {
              updateTargetProject(queryData.targetDatabaseId);
            } else {
              let newData = componentDataObj.componentData.data;
              newData.queryData.forEach((q) => {
                if (q.value === null && q.columnName) {
                  const column = newData.availableCols.find((col) => col.name === q.columnName);
                  q.value = column.placeholderKey;
                }
                if (q.condition) {
                  if (q.condition.length > 0) {
                    q.condition.forEach((cond) => {
                      if (cond.key === null && cond.columnName) {
                        const column = newData.availableCols.find((col) => col.name === cond.columnName);
                        cond.key = column.placeholderKey;
                      }
                    });
                  }
                }
              });
            }

            store.getState().setUpdateDatabaseRecordTemplate(componentDataObj);
            store.setState({ loaded: true });
            setLoadingScreen(false);
          } else {
            store
              .getState()
              .setUpdateDatabaseRecordTemplate(response.data.components[0]);
            store.setState({ loaded: true });
            setLoadingScreen(false);
          }

          props.setChildTemplateLoaded(true);
        });
      }
    });
  }, []);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder['selected'] || typeof placeholder['selected'] !== 'object') return;
        const { style, key, isNotPlaceholder } = placeholder['selected'];
        insertPlaceholder(key, isNotPlaceholder);
      }
    );
    return unsub;
  }, [thisFocus]);

  const getHeader = useCallback((name, type) => {
    const IconComponent = {
      VARCHAR2: TextIcon,
      DECIMAL: NumberIcon,
      BOOLEAN: BooleanIcon,
      DATETIME: DatetimeIcon,
      LIST: ListIcon,
    }[type];

    return () => (
      <div className="columnHeader">
        {IconComponent && <IconComponent />}
        <div>{name}</div>
      </div>
    );
  }, []);

  const getColumnType = (type) => {
    switch (type) {
      case 'DECIMAL':
        return 'number';
      case 'BOOLEAN':
        return 'boolean';
      case 'DATETIME':
        return 'dateTime';
      case 'LIST':
      case 'VARCHAR2':
      default:
        return 'string';
    }
  };

  const convertStringToArray = (s) => {
    if (s == null || s === '') return [];
    if (s.length >= 2 && s[0] === '[' && s[s.length - 1] === ']') {
      return s.slice(1, s.length - 1).split(',');
    } else return s.split(',');
  };

  const renderListCell = (params) => {
    const value = params.value;
    const items = convertStringToArray(value);
    if (!items || items.length === 0) return null;
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', py: 0.5 }}>
        {items.map((item, idx) => (
          <Chip key={idx} label={item} size="small" />
        ))}
      </Box>
    );
  };

  const updateTargetProject = async (targetDatabaseId) => {
    const cols = [
      {
        field: 'row_id',
        headerName: '',
        renderHeader: getHeader('', 'DECIMAL'),
        width: 50,
        editable: false,
        sortable: false,
        disableColumnMenu: true,
        type: 'number',
        orderNumber: 0,
      },
      {
        field: 'record_id',
        headerName: 'Record ID',
        renderHeader: getHeader('Record ID', 'VARCHAR2'),
        editable: false,
        flex: 1,
        minWidth: 200,
        type: 'string',
        columnName: 'Record ID',
        orderNumber: 1,
      },
      {
        field: 'session_id',
        headerName: 'Session ID',
        renderHeader: getHeader('Session ID', 'VARCHAR2'),
        editable: false,
        flex: 1,
        minWidth: 200,
        type: 'string',
        columnName: 'Session ID',
        orderNumber: 2,
      },
    ];

    const json = await send_request(
      `database/get_database/${targetDatabaseId}/0`,
      '',
      {},
      'POST'
    ).catch(() => {
      setIsLoading(false);
    });

    if (json && json.data.databaseId == null) {
      setIsLoading(false);
    }

    const columnJson = await send_request(
      `database/get_columns/${targetDatabaseId}`,
      '',
      {},
      'GET'
    ).catch(() => {});

    if (columnJson && columnJson.data) json['data']['columns'] = columnJson.data;

    if (json && json.status == '200') {
      if (json.data.columns) {
        for (let i = 0; i < json.data.columns.length; i++) {
          const colType = json.data.columns[i].type;
          const colDef = {
            field: json.data.columns[i].columnId,
            headerName: json.data.columns[i].name,
            renderHeader: getHeader(json.data.columns[i].name, colType),
            type: getColumnType(colType),
            baseType: colType,
            columnType: colType,
            flex: 1,
            editable: false,
            columnName: json.data.columns[i].name,
            minWidth: 180,
            orderNumber: json.data.columns[i].orderNumber,
          };
          if (colType === 'LIST') {
            colDef.renderCell = renderListCell;
          }
          if (colType === 'DATETIME') {
            colDef.valueGetter = (params) => {
              const value = params.row[params.field];
              if (!value) return null;
              const date = new Date(value);
              return isNaN(date.getTime()) ? null : date;
            };
          }
          cols.push(colDef);
        }
      }
      let newRows = [];
      if (json.data.rows) {
        if (json.data.rows.length > 0) {
          for (let i = 0; i < json.data.rows.length; i++) {
            let row = {};
            row['rowId'] = json.data.rows[i].rowId;
            row['row_id'] = i + 1;
            row['record_id'] = json.data.rows[i].rowId;
            row['session_id'] = json.data.rows[i].sessionId;
            for (let j = 0; j < json.data.rows[i].cells.length; j++) {
              row[json.data.rows[i].cells[j].columnId] = json.data.rows[i].cells[j].content;
            }
            newRows.push(row);
          }
        }
      }
      cols.sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1));

      let { queryData, availableCols } = JSON.parse(
        JSON.stringify(store.getState().data)
      );
      queryData = JSON.stringify(queryData);
      let newAvailableCols = [];
      cols.forEach((col) => {
        const sameColumn = availableCols.filter((c) => c.name === col.columnName);
        if (
          sameColumn.length > 0 &&
          sameColumn[0].placeholderKey !== 'record_id' &&
          sameColumn[0].placeholderKey !== 'session_id'
        ) {
          queryData = queryData.replaceAll(sameColumn[0].placeholderKey, col.field);
        }
        if (col.field !== 'row_id') {
          newAvailableCols.push({
            placeholderKey: col.field,
            name: col.columnName,
            type: col.baseType,
          });
        }
      });
      queryData = JSON.parse(queryData);

      store.getState().clearQueryData();
      store.getState().setQueryData(queryData);
      store.getState().setAvailableCols(newAvailableCols);

      setColumns(cols);
      setRows(newRows);
      setIsLoading(false);
    }
  };

  const insertPlaceholder = useCallback(
    (text, isNotPlaceholder) => {
      if (isNotPlaceholder !== true) {
        text = ' ${' + text + '} ';
      }

      if (!thisFocus) return;
      const storeState = store.getState();
      let { queryData } = storeState.data;
      const qd = JSON.parse(JSON.stringify(queryData));
      let cond = qd[thisFocus.index].condition[thisFocus.aDataConditionIndex];
      cond.value = text;

      updateCondition(
        thisFocus.index,
        thisFocus.aDataConditionIndex,
        cond.logic,
        cond.key,
        cond.function,
        cond.value
      );
    },
    [thisFocus]
  );

  const updateCondition = (index, conditionIndex, logic, key, func, value) => {
    const storeState = store.getState();
    const queryData = JSON.parse(JSON.stringify(storeState.data.queryData));
    const aData = queryData[index];
    if (aData.condition && aData.condition[conditionIndex]) {
      let conditionData = aData.condition[conditionIndex];
      conditionData.logic = logic;
      conditionData.key = key;
      conditionData.function = func;
      conditionData.value = value;
      store.getState().setQueryData(queryData);
    }
  };

  const keyAuto = () => 'query_data' + (Math.floor(Math.random() * 90000000) + 10000).toString();

  const addEmptyQueryItem = () => {
    const storeState = store.getState();
    const queryData = JSON.parse(JSON.stringify(storeState.data.queryData));
    const availableCols = storeState.data.availableCols;
    const firstColumnPlaceholderKey = availableCols[0]?.placeholderKey;
    const aData = {
      key: keyAuto(),
      outputFunc: 'SELECTED_COLUMN',
      outputType: 'list',
      value: firstColumnPlaceholderKey ? firstColumnPlaceholderKey : '',
    };
    queryData.push(aData);
    store.getState().setQueryData(queryData);
  };

  const copyThisItem = (data) => {
    const storeState = store.getState();
    const queryData = JSON.parse(JSON.stringify(storeState.data.queryData));
    const aData = { key: keyAuto(), ...data };
    queryData.push(aData);
    store.getState().setQueryData(queryData);
  };

  const { targetDatabaseId, availableCols, queryData } = storeData;

  if (!loadingScreen) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <CustomPaper className="query-item-paper defaultText">
            <SelectProject
              databaseList={databaseList}
              updateTargetProject={updateTargetProject}
              targetDatabaseId={targetDatabaseId}
              setTargetDatabaseId={(id) => {
                store.getState().setTargetDatabaseId(id);
                useProjectStore.setState({ targetDataBaseId: id });
              }}
              component={'query_database_record'}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              updateDatabaseList={setDatabaseList}
            />
          </CustomPaper>
        </Grid>

        <Grid item xs={12} spacing={1}>
          {queryData.map((aData, index) => (
            <RenderQueryItem
              key={aData.key || index}
              aData={aData}
              index={index}
              queryData={queryData}
              availablePlaceholders={props.availablePlaceholders}
              availableCols={availableCols}
              onPlaceholderSelected={props.onPlaceholderSelected}
              setQueryData={(qd) => {
                store.getState().setQueryData(qd);
              }}
              setThisFocus={(idx, aDataConditionIndex) => {
                setThisFocus({ index: idx, aDataConditionIndex });
              }}
              copyThisItem={() => copyThisItem(aData)}
              component={'query_database_record'}
              outputSelect={[
                { key: QUERY_OUTPUT_TYPES.SELECTED_COLUMN, name: 'all values in a column' },
                {
                  key: QUERY_OUTPUT_TYPES.FILTERED_COLUMN,
                  name: 'all values in a column that meet a condition',
                },
                {
                  key: QUERY_OUTPUT_TYPES.SELECTED_RECORD,
                  name: 'all rows that match record ID(s)',
                },
                {
                  key: QUERY_OUTPUT_TYPES.FILTERED_RECORD,
                  name: 'all rows that meet a condition',
                },
              ]}
              validateKey={validateKey}
              setValidateKey={setValidateKey}
            />
          ))}
        </Grid>

        <Grid item xs={12}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '16px',
              marginLeft: '32px',
              marginTop: '8px',
            }}
          >
            <div className="query-database-add-data" onClick={addEmptyQueryItem}>
              <AddIcon /> Add Data Query
            </div>
          </div>
        </Grid>

        {columns.length > 0 && (
          <Grid item xs={12} direction={'row'} alignItems={'center'} spacing={1}>
            <CustomPaper sx={paperStyles}>
              <Box sx={{ height: 600, width: '100%' }}>
                <DataGrid
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row.rowId}
                  pageSizeOptions={[10, 25, 50]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                  }}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeader': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                />
              </Box>
            </CustomPaper>
          </Grid>
        )}
      </Grid>
    );
  } else {
    return <IndividualComponentLoader />;
  }
};

export default QueryDatabaseTemplate;
